export type StripeEntityImpact = {
    label: string;
    affectedReports: string[];
    explanation: string;
};

/**
 * Business impact when a Stripe sync entity is missing from the secret key scope.
 * Explanations describe what revenue reports and decisions are affected for the user.
 */
export const STRIPE_ENTITY_IMPACT: Record<string, StripeEntityImpact> = {
    customers: {
        label: 'Customers',
        affectedReports: [
            'MRR & ARR account level',
            'MRR movements',
            'Revenue ↔ product correlation',
            'Customer revenue lists',
        ],
        explanation:
            'Customers are the foundation for tying payments to accounts. If customer data is missing, revenue cannot be matched to accounts in your CRM or product analytics. Metrics like account-level MRR, customer counts in movement reports, and cross-team revenue attribution will be incomplete or blank.',
    },
    invoices: {
        label: 'Invoices',
        affectedReports: [
            'Monthly recurring revenue (MRR)',
            'Total revenue',
            'Non-recurring revenue',
            'Account billing history',
        ],
        explanation:
            'Invoices record what was billed and to whom. Without invoices, payments cannot be tied to billing periods or line items. MRR, total revenue, and one-time revenue figures will be understated for any business that bills through Stripe invoices.',
    },
    invoice_items: {
        label: 'Invoice_items',
        affectedReports: [
            'One-time / Non-Recurring revenue',
        ],
        explanation:
            'Invoice items capture ad-hoc charges, credits, and adjustments added outside a standard subscription invoice. If this data is missing, one-off charges and invoice-level adjustments may not appear in revenue reports, causing total revenue and non-recurring revenue to look lower than reality.',
    },
    invoice_line_items: {
        label: 'Invoice_line_items',
        affectedReports: [
            'MRR',
            'New, Expansion & Contraction MRR',
            'Non-recurring revenue'
        ],
        explanation:
            'Invoice line items show what customers are actually paying for on each bill — plan, amount, and billing period. Without them, the system cannot split subscription vs one-time revenue or calculate new and expansion MRR from invoice-based billing. Core MRR and movement metrics will be missing or wrong.',
    },
    plans: {
        label: 'Plans',
        affectedReports: [
            'Plan level MRR',
            'Plan performance trends',
            'MRR movement drill-down',
        ],
        explanation:
            'Plans define how offerings are priced and billed (e.g. monthly vs annual). If plan data is missing, MRR can still total at a company level in some setups, but you will not see which plans drive revenue. Plan-level trends, movement labels, and pricing-tier analysis will be unavailable.',
    },
    prices: {
        label: 'Prices',
        affectedReports: [
            'MRR',
            'New MRR',
            'ARR',
            'Revenue by price point',
        ],
        explanation:
            'Prices define the billing amount and cadence for each product. Without prices, recurring amounts cannot be normalized to a monthly figure. New MRR, MRR growth, and ARR will be understated or omitted for customers billed through Stripe Prices.',
    },
    products: {
        label: 'Products',
        affectedReports: [
            'Revenue',
            'MRR movement details',
        ],
        explanation:
            'Missing product data does not always block top-line MRR, but product names in reports and breakdowns by product line will be empty. You cannot see which products contribute most to revenue or churn.',
    },
    subscriptions: {
        label: 'Subscriptions',
        affectedReports: [
            'MRR & ARR',
            'Total revenue',
            'Churn & contraction MRR',
            'Reactivation MRR',
            'Active subscriber metrics',
        ],
        explanation:
            'In subscription management, if subscription data is missing, metrics like monthly recurring revenue, annual recurring revenue, and total revenue will not be calculated correctly. Churn, downgrades, reactivations, and active subscriber counts also depend on knowing subscription status and lifecycle.',
    },
    subscription_items: {
        label: 'Subscription_items',
        affectedReports: [
            'MRR per subscription line',
            'Expansion & contraction by quantity',
            'Multi-product MRR',
        ],
        explanation:
            'Subscription items represent each product or seat on a customer’s subscription. Without them, quantity-based expansion and contraction are invisible, and MRR for customers on multi-item subscriptions may be calculated incorrectly or shown as a single undifferentiated amount.',
    },
    subscriptions_items: {
        label: 'Subscriptions_items',
        affectedReports: [
            'MRR per subscription item',
            'Expansion & contraction by quantity',
            'Multi-product MRR',
        ],
        explanation:
            'Subscription items represent each product or seat on a customer’s subscription. Without them, quantity-based expansion and contraction are invisible, and MRR for customers on multi-item subscriptions may be calculated incorrectly or shown as a single undifferentiated amount.',
    },
    balance_transactions: {
        label: 'Balance_transactions',
        affectedReports: [
            'Net revenue after fees',
            'MRR & total revenue (fee-adjusted)',
            'Payout reconciliation',
        ],
        explanation:
            'Balance transactions record what actually lands in your Stripe balance after processing fees. If this data is missing and you exclude fees from revenue, reported MRR and total revenue may be overstated because payment processing costs cannot be deducted.',
    },
    transactions: {
        label: 'Transactions',
        affectedReports: [
            'MRR (transaction-based setups)',
            'Total & non-recurring revenue',
            'Payment history',
            'New MRR from first payments',
        ],
        explanation:
            'Transactions are individual payment events. For integrations that rely on payment transactions rather than invoices alone, missing transaction data means no payment history, no new MRR from first-time payers, and incomplete total revenue for charge-based billing.',
    },
    charges: {
        label: 'Charges',
        affectedReports: [
            'Total revenue',
            'Non-recurring revenue',
            'Payment success & failure tracking',
            'New MRR (charge-based billing)',
        ],
        explanation:
            'Charges are successful (or attempted) card payments. Without charge data, cash collected through Stripe will not flow into revenue reports. Total revenue, one-time purchases, and transaction-based MRR will appear zero or incomplete even if subscriptions exist.',
    },
    disputes: {
        label: 'Disputes',
        affectedReports: [
            'Net revenue accuracy',
            'Revenue health & risk',
            'Collected vs disputed revenue',
        ],
        explanation:
            'Disputes are chargebacks — revenue customers contest with their bank. If dispute data is missing, collected revenue may look higher than what you actually keep. Risk and reconciliation views will not reflect lost or at-risk revenue from disputes.',
    },
    refunds: {
        label: 'Refunds',
        affectedReports: [
            'Net revenue',
            'Total revenue',
            'Contraction & downgrade impact',
            'Gross vs net retention',
        ],
        explanation:
            'Refunds return money to customers and reduce realized revenue. Without refund data, total and net revenue stay inflated after cancellations or downgrades with refunds. Retention and contraction metrics may overstate revenue you actually retained.',
    },
    payment_intent: {
        label: 'Payment_intent',
        affectedReports: [
            'Payment status visibility',
            'Failed payment analysis',
            'Dispute & refund linkage',
        ],
        explanation:
            'Payment intents represent the full payment attempt lifecycle (succeeded, failed, requires action). Missing access limits visibility into failed payments and incomplete checkouts. Disputes and refunds may be harder to tie back to the original payment in revenue diagnostics.',
    },
};

const ENTITY_ALIASES: Record<string, string> = {
    subscriptions_items: 'subscription_items',
};

export function getStripeEntityImpact(entity: string): StripeEntityImpact | undefined {
    const key = ENTITY_ALIASES[entity] ?? entity;
    return STRIPE_ENTITY_IMPACT[key] ?? STRIPE_ENTITY_IMPACT[entity];
}

export function normalizeMissingEntityName(entity: string): string {
    return ENTITY_ALIASES[entity] ?? entity;
}

/** Pin `subscriptions` first when present in the missing-entities list. */
export function sortMissingEntitiesForDisplay(entities: string[]): string[] {
    if (!entities.includes('subscriptions')) {
        return entities;
    }
    return ['subscriptions', ...entities.filter(e => e !== 'subscriptions')];
}
